import { useEffect } from "react";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NumericFormat as NumberFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../components/AnimatedCol";
import ConvertToString from "../../../components/Converter";
import KiloNewton from "../../../components/Math/Units/Metric/KiloNewton";
import RenderFor, { BM_TYP } from "../../../components/RenderFor";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import { useGetCharakteristischenEinwirkungenQuery } from "../../../services/API/titanApi";
import {
  selectGuenstigeStaendigeEinwirkungen,
  selectModifier,
  selectStaendigeEinwirkungen,
  selectStaendigeEinwirkungenAusErdruhedruck,
  selectUnguenstigeVeraenderlicheEinwirkungen,
  setGAMMA_G,
  setGAMMA_G_E0,
  setGAMMA_G_INF,
  setGAMMA_Q,
  setGuenstigeStaendigeEinwirkungen,
  setStaendigeEinwirkungen,
  setStaendigeEinwirkungenAusErdruhedruck,
  setUnguenstigeVeraenderlicheEinwirkungen,
} from "../../../store/slices/angepassteBemessungslastSlice";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "../../../store/slices/bemessungenMikropfaehleSlice";
import { setBemessungslast } from "../../../store/slices/bemessungslastSlice";
import { selectFrontendZustand } from "../../../store/slices/frontendZustandSlice";
import {
  BemessungssituationState,
  selectBemessungssituation,
} from "../../../store/slices/innereSlice";

export default function AngepassteBemessungslast() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const { angepassteBemessungslast: checkBoxChecked } = useSelector(selectFrontendZustand);

  const { GAMMA_G, GAMMA_Q, GAMMA_G_E0, GAMMA_G_INF } = useSelector(selectModifier);

  const staendigeEinwirkungen = useSelector(selectStaendigeEinwirkungen);
  const unguenstigeVeraenderlicheEinwirkungen = useSelector(
    selectUnguenstigeVeraenderlicheEinwirkungen
  );
  const staendigeEinwirkungenAusErdruhedruck = useSelector(
    selectStaendigeEinwirkungenAusErdruhedruck
  );
  const guenstigeStaendigeEinwirkungen = useSelector(selectGuenstigeStaendigeEinwirkungen);
  const bemessungssituation = useSelector(selectBemessungssituation);
  const { data } = useGetCharakteristischenEinwirkungenQuery(
    bemessungssituation == BemessungssituationState.DAUERHAFT ? "dauerhaft" : "temporär"
  );
  const bemessungenMikropfaehle = useSelector(selectBemessungenMikropfaehle);

  let gammaG;
  let gammaQ;
  let gammaGE0;
  let gammaGINF;
  if (bemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7) {
    gammaG = GAMMA_G;
    gammaQ = GAMMA_Q;
    gammaGE0 = GAMMA_G_E0;
    gammaGINF = GAMMA_G_INF;
  }

  if (bemessungenMikropfaehle === BemessungenMikropfaehle.GERMANY) {
    gammaG = data?.GAMMA_G;
    gammaQ = data?.GAMMA_Q;
    gammaGE0 = data?.GAMMA_G_E0;
    gammaGINF = data?.GAMMA_G_INF;
  }

  // Berechnen sobald möglich
  let angepassteBemessungslast = 0;
  if (staendigeEinwirkungen) angepassteBemessungslast += staendigeEinwirkungen * gammaG;
  if (unguenstigeVeraenderlicheEinwirkungen)
    angepassteBemessungslast += unguenstigeVeraenderlicheEinwirkungen * gammaQ;
  if (staendigeEinwirkungenAusErdruhedruck)
    angepassteBemessungslast += staendigeEinwirkungenAusErdruhedruck * gammaGE0;
  if (guenstigeStaendigeEinwirkungen)
    angepassteBemessungslast -= guenstigeStaendigeEinwirkungen * gammaGINF;

  // fix floating point number precision
  angepassteBemessungslast = Math.round(angepassteBemessungslast * 100) / 100;

  useEffect(() => {
    if (checkBoxChecked) {
      dispatch(setBemessungslast(angepassteBemessungslast));
    }
  }, [angepassteBemessungslast]);

  return (
    <Col md={8} className="highlight_box">
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p>{t("inner.fdCalculation.adjustedDesignLoad.permanentGeneral")}</p>
          </div>
          <div className="col-1">
            <span>
              E<sub>k,G</sub>
            </span>
          </div>
          <div className="col-2">
            <NumberFormat
              className="change-input"
              style={{ width: "80px" }}
              value={staendigeEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setStaendigeEinwirkungen(value.floatValue))}
              allowNegative={true}
            />
            <span>
              &nbsp;[
              <KiloNewton />]
            </span>
          </div>
          <div className="col-4">
            <RenderFor types={[BM_TYP.EUROCODE_7]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G</sub>
                </span>
                &nbsp;
                <NumberFormat
                  className="change-input"
                  style={{ width: "60px" }}
                  value={gammaG}
                  decimalSeparator={decimalSeparator()}
                  displayType="input"
                  type="text"
                  onValueChange={(value) => dispatch(setGAMMA_G(value.floatValue))}
                  thousandSeparator={thousandSeparator()}
                  allowNegative={true}
                />
              </span>
            </RenderFor>

            <RenderFor types={[BM_TYP.GERMANY]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G</sub>
                </span>
                &nbsp;
                <span className="vlera">{ConvertToString(gammaG, 2)}</span>
              </span>
            </RenderFor>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p> {t("inner.fdCalculation.adjustedDesignLoad.unfavourable")}</p>
          </div>
          <div className="col-1">
            <span>
              E<sub>k,Q</sub>
            </span>
          </div>
          <div className="col-2">
            <NumberFormat
              className="change-input"
              style={{ width: "80px" }}
              value={unguenstigeVeraenderlicheEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setUnguenstigeVeraenderlicheEinwirkungen(value.floatValue))
              }
              allowNegative={true}
            />
            <span>
              &nbsp;[
              <KiloNewton />]
            </span>
          </div>
          <div className="col-4">
            <RenderFor types={[BM_TYP.EUROCODE_7]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>Q</sub>
                </span>
                &nbsp;
                <NumberFormat
                  className="change-input"
                  style={{ width: "60px" }}
                  value={gammaQ}
                  decimalSeparator={decimalSeparator()}
                  thousandSeparator={thousandSeparator()}
                  displayType="input"
                  type="text"
                  onValueChange={(value) => dispatch(setGAMMA_Q(value.floatValue))}
                  allowNegative={true}
                />
              </span>
            </RenderFor>

            <RenderFor types={[BM_TYP.GERMANY]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>Q</sub>
                </span>
                &nbsp;
                <span className="vlera">{ConvertToString(gammaQ, 2)}</span>
              </span>
            </RenderFor>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p>{t("inner.fdCalculation.adjustedDesignLoad.permanentPressure")}</p>
          </div>
          <div className="col-1">
            <span>
              E<sub>k,E0g</sub>
            </span>
          </div>
          <div className="col-2">
            <NumberFormat
              className="change-input"
              style={{ width: "80px" }}
              value={staendigeEinwirkungenAusErdruhedruck}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setStaendigeEinwirkungenAusErdruhedruck(value.floatValue))
              }
              allowNegative={true}
            />
            <span>
              &nbsp;[
              <KiloNewton />]
            </span>
          </div>
          <div className="col-4">
            <RenderFor types={[BM_TYP.EUROCODE_7]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G,E0</sub>
                </span>
                &nbsp;
                <NumberFormat
                  className="change-input"
                  style={{ width: "60px" }}
                  value={gammaGE0}
                  decimalSeparator={decimalSeparator()}
                  thousandSeparator={thousandSeparator()}
                  displayType="input"
                  type="text"
                  onValueChange={(value) => dispatch(setGAMMA_G_E0(value.floatValue))}
                  allowNegative={true}
                />
              </span>
            </RenderFor>

            <RenderFor types={[BM_TYP.GERMANY]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G,E0</sub>
                </span>
                &nbsp;
                <span className="vlera">{ConvertToString(gammaGE0, 2)}</span>
              </span>
            </RenderFor>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p> {t("inner.fdCalculation.adjustedDesignLoad.favourable")}</p>
          </div>
          <div className="col-1">
            <span>
              E<sub>k,G,inf</sub>
            </span>
          </div>
          <div className="col-2">
            <NumberFormat
              className="change-input"
              style={{ width: "80px" }}
              value={guenstigeStaendigeEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setGuenstigeStaendigeEinwirkungen(value.floatValue))
              }
              allowNegative={true}
            />
            <span>
              &nbsp;[
              <KiloNewton />]
            </span>
          </div>
          <div className="col-4">
            <RenderFor types={[BM_TYP.EUROCODE_7]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G,inf</sub>
                </span>
                &nbsp;
                <NumberFormat
                  className="change-input"
                  style={{ width: "60px" }}
                  value={gammaGINF}
                  decimalSeparator={decimalSeparator()}
                  thousandSeparator={thousandSeparator()}
                  displayType="input"
                  type="text"
                  onValueChange={(value) => dispatch(setGAMMA_G_INF(value.floatValue))}
                  allowNegative={true}
                />
              </span>
            </RenderFor>
            <RenderFor types={[BM_TYP.GERMANY]}>
              <span className="angepassteBemessunglast--input--formula">
                <span>
                  &gamma;<sub>G,inf</sub>
                </span>
                &nbsp;
                <span className="vlera">{ConvertToString(gammaGINF, 2)}</span>
              </span>
            </RenderFor>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
    </Col>
  );
}
